import React from "react";
import {
  FaMicrochip, FaTimes, FaBuilding,
  FaCoffee, FaCogs, FaTint,
  FaCircle,
  FaArrowUp, FaArrowDown, FaSort,
} from "react-icons/fa";
import { AppLink } from "../shared";
import { TruckIcon } from "@heroicons/react/24/outline";
import { getDeviceCupNumber, getGrinderStatus } from "../../utils/deviceUtils";
import Drawer from "../shared/Drawer";
import SearchInput from "../shared/SearchInput";

import turkishCoffeeMachine from "../../Assets/icons/turkish-coffee-machine.png";
import teaFilterCoffeeMachine from "../../Assets/icons/tea-filter-coffee-machine-icon.png";
import teaTurkishCoffeeMachine from "../../Assets/icons/tea_coffee_machine.png";
import { deviceActivityStateLabel } from "../../utils/deviceActivityLabels";
import { getElapsed, formatElapsed } from "../../utils/elapsed";

// Cihaz listesindeki makine görselleriyle aynı set
const DEVICE_IMAGES = {
  Fincanda: turkishCoffeeMachine,
  Çayset: teaFilterCoffeeMachine,
  "Çayset-Fincanda": teaTurkishCoffeeMachine,
};

const LONG_STAY_DAYS = 15;

const accentMap = {
  firmDevices: { dot: "bg-blue-500", badge: "bg-blue-50 text-blue-700", text: "text-blue-600", header: "bg-gradient-to-r from-blue-600 to-blue-700" },
  criticalDevices: { dot: "bg-red-500", badge: "bg-red-50 text-red-700", text: "text-red-600", header: "bg-gradient-to-r from-red-600 to-red-700" },
  unConnectedDevices: { dot: "bg-amber-500", badge: "bg-amber-50 text-amber-700", text: "text-amber-600", header: "bg-gradient-to-r from-amber-500 to-amber-600" },
  unUsedDevices: { dot: "bg-slate-500", badge: "bg-slate-50 text-slate-700", text: "text-slate-600", header: "bg-gradient-to-r from-slate-600 to-slate-700" },
  serviceDevices: { dot: "bg-indigo-500", badge: "bg-indigo-50 text-indigo-700", text: "text-indigo-600", header: "bg-gradient-to-r from-indigo-600 to-indigo-700" },
  storageDevices: { dot: "bg-emerald-500", badge: "bg-emerald-50 text-emerald-700", text: "text-emerald-600", header: "bg-gradient-to-r from-emerald-600 to-emerald-700" },
  testDevices: { dot: "bg-violet-500", badge: "bg-violet-50 text-violet-700", text: "text-violet-600", header: "bg-gradient-to-r from-violet-600 to-violet-700" },
  playmakerDevices: { dot: "bg-pink-500", badge: "bg-pink-50 text-pink-700", text: "text-pink-600", header: "bg-gradient-to-r from-pink-600 to-pink-700" },
};

const statusCategoryLabels = {
  storageDevices: "Depoda",
  serviceDevices: "Serviste",
  testDevices: "Testte",
  playmakerDevices: "Plasiyerde",
};

const statusCategories = ["storageDevices", "serviceDevices", "testDevices", "playmakerDevices"];

function getDaysSince(dateString) {
  if (!dateString) return null;
  return Math.floor((new Date() - new Date(dateString)) / (1000 * 60 * 60 * 24));
}

function formatShortDate(dateString) {
  if (!dateString) return "-";
  return new Date(dateString).toLocaleDateString("tr-TR", { day: "numeric", month: "short", year: "numeric" });
}

export default function DeviceStatusDrawer({
  selectedDevices,
  open,
  onClose,
  pagedDevices,
  page,
  setPage,
  filteredDevices,
  pageSize,
  search,
  setSearch,
  sort,
  onSort,
  tabs = [],
  onTabChange,
}) {
  if (!open || !selectedDevices) return null;

  const title = selectedDevices.title;
  const accent = accentMap[selectedDevices.type] || accentMap.firmDevices;
  const isStatusCategory = statusCategories.includes(selectedDevices.type);

  const type = selectedDevices.type;

  // Her kategorinin kendi "detay" sütunu İKİNCİ sütundur (Cihaz'dan hemen
  // sonra) ve büyük yazımla gösterilir:
  //   depo/servis/test/plasiyer → kaç gündür o durumda
  //   bağlanmayan              → kaç gündür bağlı değil
  //   kullanılmayan            → kaç gündür tüketim yok
  //   kritik kota              → kritik ürün detayları
  const detailColumn = isStatusCategory
    ? { key: "statusChangedAt", label: statusCategoryLabels[type] || "Süre" }
    : type === "unConnectedDevices"
      ? { key: "lastConnected", label: "Bağlı Değil" }
      : type === "unUsedDevices"
        ? { key: "lastConsumptionDate", label: "Tüketim Yok" }
        : type === "criticalDevices"
          ? { key: "criticalCount", label: "Kritik Kota" }
          : null;

  // Bağlanmayan kategorisinde ayrıca Bağlantı sütunu anlamsız (hepsi bağlı
  // değil; bilgi detay sütununda) — gizlenir.
  const showConnectionColumn = type !== "unConnectedDevices";

  const columns = [
    { key: "serialNo", label: "Cihaz" },
    ...(detailColumn ? [detailColumn] : []),
    ...(showConnectionColumn ? [{ key: "connection", label: "Bağlantı" }] : []),
    { key: "cupNumber", label: "Bileşenler" },
    { key: "firmName", label: "Firma", w: detailColumn ? "w-[26%]" : "w-[38%]" },
  ];


  const totalPages = Math.max(1, Math.ceil(filteredDevices.length / pageSize));

  return (
    <Drawer open={open} onClose={onClose} width="xl">
      {/* Custom colored header */}
      <Drawer.Header className={`flex items-center justify-between px-5 py-4 ${accent.header} text-white`}>
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-9 h-9 rounded-lg bg-white/20">
            <FaMicrochip className="w-4 h-4" />
          </div>
          <div>
            <h2 className="text-lg font-semibold leading-tight">{title}</h2>
            <p className="text-xs opacity-80 mt-0.5">{filteredDevices.length} cihaz</p>
          </div>
        </div>
        <button onClick={onClose} className="p-1.5 hover:bg-white/20 rounded-lg transition-colors">
          <FaTimes className="w-4 h-4" />
        </button>
      </Drawer.Header>

      {/* Kategori tab'ları — liste aynı drawer içinde değişir */}
      {tabs.length > 0 && (
        <div className="shrink-0 px-4 py-2.5 border-b border-gray-100 bg-gray-50 flex gap-1.5 overflow-x-auto">
          {tabs.map((tab) => {
            const isActive = tab.type === selectedDevices.type;
            const tabAccent = accentMap[tab.type] || accentMap.firmDevices;
            return (
              <button
                key={tab.type}
                type="button"
                onClick={() => !isActive && onTabChange?.(tab.type)}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-semibold whitespace-nowrap transition-colors ${
                  isActive
                    ? `${tabAccent.badge} border-transparent shadow-sm`
                    : "bg-white text-gray-600 border-gray-200 hover:bg-gray-100"
                }`}
              >
                {tab.label}
                <span className={`inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold leading-none ${
                  isActive ? "bg-white/80 text-gray-700" : "bg-gray-100 text-gray-500"
                }`}>
                  {tab.count}
                </span>
              </button>
            );
          })}
        </div>
      )}

      {/* Search — ayrı alanlar (seri no / cihaz adı / firma) */}
      <div className="shrink-0 px-4 py-3 border-b border-gray-100 bg-white grid grid-cols-1 sm:grid-cols-3 gap-2">
        <SearchInput
          placeholder="Seri No"
          value={search.serialNo}
          onChange={(e) => setSearch((p) => ({ ...p, serialNo: e.target.value }))}
        />
        <SearchInput
          placeholder="Cihaz Adı"
          value={search.deviceName}
          onChange={(e) => setSearch((p) => ({ ...p, deviceName: e.target.value }))}
        />
        {selectedDevices.type === "playmakerDevices" ? (
          <SearchInput
            placeholder="Plasiyer"
            value={search.playMakerName}
            onChange={(e) => setSearch((p) => ({ ...p, playMakerName: e.target.value }))}
          />
        ) : (
          <SearchInput
            placeholder="Firma"
            value={search.firmName}
            onChange={(e) => setSearch((p) => ({ ...p, firmName: e.target.value }))}
          />
        )}
      </div>

      {/* Device List */}
      <Drawer.Body>
        {pagedDevices.length === 0 ? (
          <div className="text-center py-16 px-4">
            <FaMicrochip className="w-10 h-10 text-gray-200 mx-auto mb-3" />
            <p className="text-sm text-gray-500">Cihaz bulunamadı</p>
          </div>
        ) : (
          <div>
            {/* Mobil kart görünümü — dar ekranda tablo yerine dikey kartlar */}
            <div className="sm:hidden divide-y divide-gray-100">
              {pagedDevices.map((device) => {
                const isConnected = device.deviceInfo?.connection?.status;
                const lastConn = device.deviceInfo?.connection?.lastConnected || device.lastConnectionDate;
                const fId = device.firm?._id || device.firmId;
                const devImg = DEVICE_IMAGES[device.deviceInfo?.deviceName || device.deviceName];
                const offlineDays = !isConnected ? getDaysSince(lastConn) : null;
                const statusElapsed = device.statusChangedAt ? getElapsed(device.statusChangedAt) : null;
                const noConsElapsed = getElapsed(device.lastConsumptionDate);
                const criticals = (device.productInfo?.productInfo || []).filter(
                  (p) => p.quotaDistribution && p.quota < p.quotaWarning
                );
                return (
                  <div key={device._id} className="px-4 py-3 space-y-2">
                    {/* Cihaz linki + Açık rozeti */}
                    <div className="flex items-start justify-between gap-2">
                      <AppLink
                        to={`/Anasayfa/Cihazlar/${device._id}`}
                        state={{ device, fromPage: "Anasayfa/Dashboard" }}
                        onClick={onClose}
                        className="flex items-center gap-2.5 min-w-0"
                      >
                        <div className="w-10 h-10 shrink-0 bg-slate-100 rounded-lg flex items-center justify-center overflow-hidden">
                          {devImg ? (
                            <img src={devImg} alt="" className="max-w-full max-h-full object-contain p-0.5" />
                          ) : (
                            <FaMicrochip className="w-4 h-4 text-slate-500" />
                          )}
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-bold text-gray-900 break-all">{device.serialNo || "Seri No Yok"}</p>
                          <p className="text-xs text-gray-500 truncate">{device.deviceInfo?.deviceName || device.deviceName || ""}</p>
                        </div>
                      </AppLink>
                      <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-[11px] font-semibold whitespace-nowrap shrink-0 ${device.isActive ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-600"}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${device.isActive ? "bg-emerald-500 animate-pulse" : "bg-red-500"}`} />
                        {deviceActivityStateLabel(device.isActive)}
                      </span>
                    </div>

                    {/* Kategoriye özel detay + bağlantı — kompakt satır */}
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                      {isStatusCategory && statusElapsed && (
                        <span className={`text-sm font-extrabold ${accent.text}`}>
                          {statusElapsed.value} <span className="text-xs font-bold">{statusElapsed.unit} {(statusCategoryLabels[type] || "").toLowerCase()}</span>
                        </span>
                      )}
                      {type === "unUsedDevices" && (
                        <span className={`text-sm font-extrabold ${accent.text}`}>
                          {noConsElapsed ? (
                            <>{noConsElapsed.value} <span className="text-xs font-bold">{noConsElapsed.unit} tüketim yok</span></>
                          ) : (
                            <span className="text-xs font-bold">Hiç tüketim yok</span>
                          )}
                        </span>
                      )}
                      <span className={`inline-flex items-center gap-1.5 text-[11px] font-semibold whitespace-nowrap ${isConnected ? "text-emerald-600" : "text-red-600"}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${isConnected ? "bg-emerald-500 animate-pulse" : "bg-red-500"}`} />
                        {isConnected
                          ? "Bağlı"
                          : offlineDays != null
                            ? `${formatElapsed(lastConn)} bağlı değil`
                            : "Bağlı Değil"}
                      </span>
                      {type === "playmakerDevices" && device.playMaker && (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-pink-50 text-pink-700 whitespace-nowrap">
                          <TruckIcon className="w-3 h-3" />
                          {device.playMaker.name}{device.playMaker.lastName ? ` ${device.playMaker.lastName}` : ""}
                        </span>
                      )}
                    </div>

                    {/* Kritik kota — sadece kritik kategoride */}
                    {type === "criticalDevices" && criticals.length > 0 && (
                      <div className="flex flex-wrap gap-1.5">
                        {criticals.map((p) => (
                          <span key={p.productName} className="inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded-md bg-red-50 text-red-700 border border-red-100 whitespace-nowrap">
                            {p.productName} <span className="font-bold">{p.quota}</span>/{p.quotaWarning}
                          </span>
                        ))}
                      </div>
                    )}

                    {/* Firma linki */}
                    <AppLink
                      to={fId ? `/Anasayfa/Firma/${fId}` : "/Anasayfa/Firma"}
                      state={fId ? { firm: device.firm, fromPage: "Anasayfa/Dashboard" } : { searchFirmName: device.firm?.firmName || "", fromPage: "Anasayfa/Dashboard" }}
                      onClick={onClose}
                      className="inline-flex items-center gap-1.5 text-xs font-semibold text-gray-800"
                    >
                      <FaBuilding className="w-3 h-3 text-indigo-500" />
                      <span className="truncate">{device.firm?.firmName || "—"}</span>
                      {device.firm?.bayserNo && (
                        <span className="text-[11px] font-medium text-gray-400">Bayser: {device.firm.bayserNo}</span>
                      )}
                    </AppLink>
                  </div>
                );
              })}
            </div>

            <table className="hidden sm:table w-full border-collapse">
              <thead className="sticky top-0 z-10">
                <tr className="border-b border-gray-200 text-left text-[11px] font-semibold uppercase tracking-wide text-gray-500 shadow-sm">
                  {columns.map((col) => (
                    <th key={col.key} className={`px-3 py-2.5 bg-gray-50 ${col.w || ""}`}>
                      <button
                        type="button"
                        onClick={() => onSort?.(col.key)}
                        className={`inline-flex items-center gap-1 uppercase tracking-wide hover:text-gray-700 transition-colors ${sort?.key === col.key ? "text-gray-800" : ""}`}
                      >
                        {col.label}
                        {sort?.key === col.key
                          ? (sort.dir === "asc"
                              ? <FaArrowUp className="w-2.5 h-2.5 text-indigo-600" />
                              : <FaArrowDown className="w-2.5 h-2.5 text-indigo-600" />)
                          : <FaSort className="w-2.5 h-2.5 text-gray-300" />}
                      </button>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {pagedDevices.map((device, rowIdx) => {
                  const isLongStay =
                    (selectedDevices.type === "serviceDevices" || selectedDevices.type === "playmakerDevices") &&
                    device.statusChangedAt &&
                    getDaysSince(device.statusChangedAt) >= LONG_STAY_DAYS;

                  const isConnected = device.deviceInfo?.connection?.status;
                  const lastConn = device.deviceInfo?.connection?.lastConnected || device.lastConnectionDate;
                  const hasGrinder =
                    getGrinderStatus(device).label !== "-" ||
                    (device.componentSummary?.grinderStatus &&
                      device.componentSummary.grinderStatus !== "-");
                  const fId = device.firm?._id || device.firmId;

                  return (
                    <tr
                      key={device._id}
                      className={`align-top transition-colors ${rowIdx % 2 === 1 ? "bg-gray-50/60" : "bg-white"} hover:bg-indigo-50/40`}
                    >
                      {/* Cihaz — ikon + seri + model tek link (group-hover);
                          altında Açık/Kapalı rozeti (liste sayfalarıyla aynı desen) */}
                      <td className="px-3 py-3">
                        <div className="flex flex-col gap-1 min-w-0">
                          <AppLink
                            to={`/Anasayfa/Cihazlar/${device._id}`}
                            state={{ device, fromPage: "Anasayfa/Dashboard" }}
                            onClick={onClose}
                            className="group flex items-center gap-2 min-w-0 w-fit max-w-full"
                            title="Cihaza git"
                          >
                            <div className="w-10 h-10 shrink-0 bg-slate-100 rounded-lg flex items-center justify-center overflow-hidden border border-transparent transition-colors group-hover:bg-indigo-50 group-hover:border-indigo-300">
                              {(() => {
                                const devImg = DEVICE_IMAGES[device.deviceInfo?.deviceName || device.deviceName];
                                return devImg ? (
                                  <img src={devImg} alt={device.deviceInfo?.deviceName || device.deviceName} className="max-w-full max-h-full object-contain p-0.5" />
                                ) : (
                                  <FaMicrochip className="w-4 h-4 text-slate-500 transition-colors group-hover:text-indigo-600" />
                                );
                              })()}
                            </div>
                            <div className="min-w-0">
                              <div className="flex items-center gap-1.5">
                                {isLongStay && (
                                  <span className="relative flex h-2.5 w-2.5 shrink-0">
                                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-60" />
                                    <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-red-500" />
                                  </span>
                                )}
                                <span className="text-sm font-bold text-gray-900 break-all transition-colors group-hover:text-indigo-700 group-hover:underline underline-offset-2 decoration-indigo-400">
                                  {device.serialNo || "Seri No Yok"}
                                </span>
                              </div>
                              <div className="text-xs font-medium text-gray-600 truncate max-w-[150px] transition-colors group-hover:text-indigo-500 group-hover:underline underline-offset-2 decoration-indigo-300">
                                {device.deviceInfo?.deviceName || device.deviceName || "İsimsiz Cihaz"}
                              </div>
                            </div>
                          </AppLink>
                          <span
                            className={`inline-flex w-fit items-center gap-1.5 px-2 py-0.5 rounded-md text-[11px] font-semibold whitespace-nowrap ${
                              device.isActive ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-600"
                            }`}
                          >
                            <span className={`w-1.5 h-1.5 rounded-full ${device.isActive ? "bg-emerald-500 animate-pulse" : "bg-red-500"}`} />
                            {deviceActivityStateLabel(device.isActive)}
                          </span>
                          {/* Kategoriye özel süre/kritik bilgisi artık kendi detay sütununda */}
                        </div>
                      </td>

                      {/* Detay — kategoriye özel İKİNCİ sütun, büyük yazım */}
                      {detailColumn && (
                        <td className="px-3 py-3">
                          {(() => {
                            // Depoda / Serviste / Testte / Plasiyerde
                            if (isStatusCategory) {
                              const elapsed = device.statusChangedAt ? getElapsed(device.statusChangedAt) : null;
                              return (
                                <div className="flex flex-col gap-1 whitespace-nowrap">
                                  {elapsed ? (
                                    <div className="flex items-baseline gap-1.5">
                                      <span className={`text-lg font-extrabold leading-none tabular-nums ${accent.text}`}>{elapsed.value}</span>
                                      <span className={`text-xs font-bold ${accent.text}`}>
                                        {elapsed.unit} {(statusCategoryLabels[type] || "").toLowerCase()}
                                      </span>
                                    </div>
                                  ) : (
                                    <span className="text-sm text-gray-400">—</span>
                                  )}
                                  {device.statusChangedAt && (
                                    <span className="text-[11px] text-gray-400">{formatShortDate(device.statusChangedAt)}</span>
                                  )}
                                  {type === "playmakerDevices" && device.playMaker && (
                                    <span className="inline-flex w-fit items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-pink-50 text-pink-700 whitespace-nowrap">
                                      <TruckIcon className="w-3.5 h-3.5" />
                                      {device.playMaker.name}{device.playMaker.lastName ? ` ${device.playMaker.lastName}` : ""}
                                    </span>
                                  )}
                                </div>
                              );
                            }
                            // Bağlanmayan cihazlar — kaç gündür bağlı değil
                            if (type === "unConnectedDevices") {
                              const elapsed = getElapsed(lastConn);
                              return (
                                <div className="flex flex-col gap-1 whitespace-nowrap">
                                  {elapsed ? (
                                    <div className="flex items-baseline gap-1.5">
                                      <span className={`text-lg font-extrabold leading-none tabular-nums ${accent.text}`}>{elapsed.value}</span>
                                      <span className={`text-xs font-bold ${accent.text}`}>{elapsed.unit} bağlı değil</span>
                                    </div>
                                  ) : (
                                    <span className={`text-xs font-bold ${accent.text}`}>Hiç bağlanmadı</span>
                                  )}
                                  {lastConn && (
                                    <span className="text-[11px] text-gray-400">
                                      Son: {new Date(lastConn).toLocaleString("tr-TR", { dateStyle: "short", timeStyle: "short" })}
                                    </span>
                                  )}
                                </div>
                              );
                            }
                            // Kullanılmayan cihazlar — kaç gündür tüketim yok
                            if (type === "unUsedDevices") {
                              const elapsed = getElapsed(device.lastConsumptionDate);
                              return (
                                <div className="flex flex-col gap-1 whitespace-nowrap">
                                  {elapsed ? (
                                    <div className="flex items-baseline gap-1.5">
                                      <span className={`text-lg font-extrabold leading-none tabular-nums ${accent.text}`}>{elapsed.value}</span>
                                      <span className={`text-xs font-bold ${accent.text}`}>{elapsed.unit} tüketim yok</span>
                                    </div>
                                  ) : (
                                    <span className={`text-xs font-bold ${accent.text}`}>Hiç tüketim yok</span>
                                  )}
                                  {device.lastConsumptionDate && (
                                    <span className="text-[11px] text-gray-400">Son: {formatShortDate(device.lastConsumptionDate)}</span>
                                  )}
                                </div>
                              );
                            }
                            // Kritik kotalı cihazlar — ürün bazlı kota detayları
                            if (type === "criticalDevices") {
                              const criticals = (device.productInfo?.productInfo || []).filter(
                                (p) => p.quotaDistribution && p.quota < p.quotaWarning
                              );
                              if (criticals.length === 0) return <span className="text-sm text-gray-400">—</span>;
                              return (
                                <div className="flex flex-col gap-1.5">
                                  {criticals.map((p) => (
                                    <span key={p.productName} className="inline-flex w-fit items-baseline gap-1.5 px-2 py-1 rounded-lg bg-red-50 border border-red-100 whitespace-nowrap">
                                      <span className="text-xs font-semibold text-gray-700">{p.productName}</span>
                                      <span className="text-sm font-extrabold leading-none text-red-600 tabular-nums">{p.quota}</span>
                                      <span className="text-[11px] font-medium text-red-400">/ {p.quotaWarning}</span>
                                    </span>
                                  ))}
                                </div>
                              );
                            }
                            return null;
                          })()}
                        </td>
                      )}

                      {/* Bağlantı — bağlı değilse kaç gündür bağlı olmadığı yazılır */}
                      {showConnectionColumn && (
                      <td className="px-3 py-3">
                        {(() => {
                          const offlineDays = !isConnected ? getDaysSince(lastConn) : null;
                          return (
                            <span className={`inline-flex gap-1 text-[11px] font-medium px-1.5 py-0.5 rounded-md border ${isConnected ? "items-center whitespace-nowrap bg-emerald-50 text-emerald-700 border-emerald-100" : "flex-col items-start bg-red-50 text-red-700 border-red-100"}`}>
                              <span className="inline-flex items-center gap-1 whitespace-nowrap">
                                <span className="relative flex h-2 w-2 shrink-0">
                                  {isConnected && <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />}
                                  <FaCircle className={`relative inline-flex h-2 w-2 ${isConnected ? "text-emerald-500" : "text-red-500"}`} />
                                </span>
                                {isConnected
                                  ? "Bağlı"
                                  : offlineDays != null
                                    ? `${formatElapsed(lastConn)} bağlı değil`
                                    : "Bağlı Değil"}
                              </span>
                              {!isConnected && lastConn && (
                                <span className="text-[10px] font-normal text-red-700/70 whitespace-nowrap">
                                  {new Date(lastConn).toLocaleString("tr-TR", { dateStyle: "short", timeStyle: "short" })}
                                </span>
                              )}
                            </span>
                          );
                        })()}
                      </td>
                      )}

                      {/* Bileşenler */}
                      <td className="px-3 py-3">
                        <div className="flex flex-wrap gap-1.5">
                          <span className="inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded-md bg-slate-50 text-slate-700 border border-slate-200 whitespace-nowrap">
                            <FaCoffee className="w-2.5 h-2.5 shrink-0" /> {getDeviceCupNumber(device)} göz
                          </span>
                          {hasGrinder && (
                            <span className="inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded-md bg-amber-50 text-amber-700 border border-amber-100 whitespace-nowrap">
                              <FaCogs className="w-2.5 h-2.5 shrink-0" /> Değirmenli
                            </span>
                          )}
                          {device.componentSummary?.waterModeStatus && device.componentSummary.waterModeStatus !== "-" && (
                            <span className="inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded-md bg-blue-50 text-blue-700 border border-blue-100 whitespace-nowrap">
                              <FaTint className="w-2.5 h-2.5 shrink-0" /> {device.componentSummary.waterModeStatus}
                            </span>
                          )}
                        </div>
                      </td>

                      {/* Firma */}
                      <td className="px-3 py-3">
                        <div className="flex flex-col items-start gap-1">
                          <AppLink
                            to={fId ? `/Anasayfa/Firma/${fId}` : "/Anasayfa/Firma"}
                            state={fId ? { firm: device.firm, fromPage: "Anasayfa/Dashboard" } : { searchFirmName: device.firm?.firmName || "", fromPage: "Anasayfa/Dashboard" }}
                            onClick={onClose}
                            className="group flex items-center gap-2 min-w-0 w-fit max-w-full"
                            title="Firmaya git"
                          >
                            <div className="flex items-center justify-center shrink-0 w-7 h-7 rounded-lg bg-indigo-50 border border-indigo-100 transition-colors group-hover:bg-indigo-100 group-hover:border-indigo-300">
                              <FaBuilding className="w-3.5 h-3.5 text-indigo-500 transition-colors group-hover:text-indigo-700" />
                            </div>
                            <div className="flex flex-col min-w-0">
                              <span className="text-xs font-semibold text-gray-900 break-words leading-snug transition-colors group-hover:text-indigo-700 group-hover:underline underline-offset-2 decoration-indigo-400">
                                {device.firm?.firmName || "—"}
                              </span>
                              {device.firm?.bayserNo && (
                                <span className="text-[11px] font-medium text-gray-400 truncate transition-colors group-hover:text-indigo-500 group-hover:underline underline-offset-2 decoration-indigo-300">
                                  Bayser: {device.firm.bayserNo}
                                </span>
                              )}
                            </div>
                          </AppLink>
                          {/* Plasiyer kategorisinde plasiyer adı süre sütununda gösterilir */}
                          {selectedDevices.type !== "playmakerDevices" && device.playMaker && (
                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-pink-50 text-pink-700 whitespace-nowrap">
                              <TruckIcon className="w-3 h-3" />
                              {device.playMaker.name}{device.playMaker.lastName ? ` ${device.playMaker.lastName}` : ""}
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Drawer.Body>

      {/* Pagination */}
      <Drawer.Footer className="px-4 py-3 border-t border-gray-100 bg-white">
        <div className="flex items-center justify-between">
          <p className="text-xs text-gray-400">
            {filteredDevices.length > 0
              ? `${(page - 1) * pageSize + 1}-${Math.min(page * pageSize, filteredDevices.length)} / ${filteredDevices.length}`
              : "0 / 0"}
          </p>
          <div className="flex items-center gap-1.5">
            <button
              disabled={page <= 1}
              onClick={() => setPage(page - 1)}
              className="px-3 py-1.5 text-xs font-medium text-gray-600 bg-white border border-gray-200 rounded-md hover:bg-gray-50 disabled:opacity-40 transition-colors"
            >
              Önceki
            </button>
            <span className="px-2.5 py-1.5 text-xs font-medium text-gray-500 bg-gray-50 rounded-md">
              {page}/{totalPages}
            </span>
            <button
              disabled={page >= totalPages || filteredDevices.length === 0}
              onClick={() => setPage(page + 1)}
              className="px-3 py-1.5 text-xs font-medium text-gray-600 bg-white border border-gray-200 rounded-md hover:bg-gray-50 disabled:opacity-40 transition-colors"
            >
              Sonraki
            </button>
          </div>
        </div>
      </Drawer.Footer>
    </Drawer>
  );
}
